import type { Majority, Ratio, RuleSet } from '@simulador/engine';

// Plantillas para partir al crear reglas. Son aproximaciones de reglamentos reales para simular,
// no su transcripción: cada organización debe revisarlas contra el suyo. Este módulo no importa
// Ajv, así que el front lo puede usar sin cargar el validador.

export type RuleSetTemplate = {
  id: string;
  name: string;
  /** En qué se basa y qué simplifica. */
  basis: string;
  ruleSet: RuleSet;
};

const HALF: Ratio = { num: 1, den: 2 };
const TWO_THIRDS: Ratio = { num: 2, den: 3 };
const THREE_QUARTERS: Ratio = { num: 3, den: 4 };

const majority = (label: string, base: Majority['base'], threshold: Ratio, strict: boolean, abstentionsCount: boolean): Majority =>
  ({ label, base, threshold, strict, abstentionsCount });

export const RULESET_TEMPLATES: readonly RuleSetTemplate[] = [
  {
    id: 'diputados',
    name: 'Cámara de Diputados',
    basis: 'Basada en la Constitución (arts. 63 y 135), la Ley Orgánica del Congreso y el Reglamento de la Cámara de Diputados. '
      + 'El empate se repite; la segunda vuelta en otra sesión queda a cargo de la presidencia.',
    ruleSet: {
      version: 'v1.0',
      quorum: { type: 'percent', value: HALF, strict: true, atClose: 'ballots' },
      tie: 'repeat',
      mode: 'nominal',
      majorities: {
        simple: majority('Mayoría simple', 'emitted', HALF, true, false),
        absoluta: majority('Mayoría absoluta de los presentes', 'present', HALF, true, true),
        calificada: majority('Dos tercios de los presentes', 'present', TWO_THIRDS, false, true),
      },
      matters: [
        { id: 'ley', label: 'Ley o decreto', majority: 'simple' },
        { id: 'acuerdo', label: 'Punto de acuerdo', majority: 'simple' },
        { id: 'constitucional', label: 'Reforma constitucional', majority: 'calificada' },
        { id: 'mesa', label: 'Elección de la Mesa Directiva', majority: 'calificada', mode: 'secreta' },
      ],
    },
  },
  {
    id: 'senado',
    name: 'Senado',
    basis: 'Basada en la Constitución (arts. 63 y 135) y el Reglamento del Senado. Igual que Diputados, pero sin quórum al cerrar.',
    ruleSet: {
      version: 'v1.0',
      quorum: { type: 'percent', value: HALF, strict: true, atClose: 'none' },
      tie: 'repeat',
      mode: 'nominal',
      majorities: {
        simple: majority('Mayoría simple', 'emitted', HALF, true, false),
        absoluta: majority('Mayoría absoluta de los presentes', 'present', HALF, true, true),
        calificada: majority('Dos tercios de los presentes', 'present', TWO_THIRDS, false, true),
      },
      matters: [
        { id: 'ley', label: 'Ley o decreto', majority: 'simple' },
        { id: 'nombramiento', label: 'Ratificación de nombramiento', majority: 'simple' },
        { id: 'tratado', label: 'Aprobación de tratado', majority: 'simple' },
        { id: 'constitucional', label: 'Reforma constitucional', majority: 'calificada' },
        { id: 'mesa', label: 'Elección de la Mesa Directiva', majority: 'absoluta', mode: 'secreta' },
      ],
    },
  },
  {
    id: 'onu',
    name: 'Modelo ONU (Asamblea General)',
    basis: 'Basada en el reglamento de la Asamblea General: "miembros presentes y votantes" (las abstenciones no cuentan), '
      + 'cuestiones importantes por dos tercios, elecciones en secreto y empate que rechaza la propuesta.',
    ruleSet: {
      version: 'v1.0',
      quorum: { type: 'percent', value: HALF, strict: true, atClose: 'none' },
      tie: 'reject',
      mode: 'nominal',
      majorities: {
        simple: majority('Mayoría de presentes y votantes', 'emitted', HALF, true, false),
        importante: majority('Dos tercios de presentes y votantes', 'emitted', TWO_THIRDS, false, false),
      },
      matters: [
        { id: 'resolucion', label: 'Proyecto de resolución', majority: 'simple' },
        { id: 'enmienda', label: 'Enmienda', majority: 'simple' },
        { id: 'procedimiento', label: 'Moción de procedimiento', majority: 'simple' },
        { id: 'importante', label: 'Cuestión importante', majority: 'importante' },
        { id: 'eleccion', label: 'Elección', majority: 'simple', mode: 'secreta' },
      ],
    },
  },
  {
    id: 'condominio',
    name: 'Asamblea de condominio',
    basis: 'Basada en la Ley de Propiedad en Condominio de la CDMX (quórum de segunda convocatoria). '
      + 'Un voto por persona: no pondera por indiviso.',
    ruleSet: {
      version: 'v1.0',
      quorum: { type: 'percent', value: HALF, strict: true, atClose: 'present' },
      tie: 'reject',
      mode: 'nominal',
      majorities: {
        simple: majority('Mayoría de los presentes', 'present', HALF, true, true),
        'tres-cuartos': majority('Tres cuartas partes del total', 'total', THREE_QUARTERS, false, true),
      },
      matters: [
        { id: 'general', label: 'Asunto general', majority: 'simple' },
        { id: 'cuota', label: 'Cuota de mantenimiento', majority: 'simple' },
        { id: 'obra', label: 'Obra mayor', majority: 'tres-cuartos' },
        { id: 'reglamento', label: 'Cambio al reglamento interno', majority: 'tres-cuartos' },
        { id: 'administracion', label: 'Nombramiento de la administración', majority: 'simple' },
      ],
    },
  },
  {
    id: 'asociacion',
    name: 'Asociación civil',
    basis: 'Basada en el Código Civil Federal (art. 2677: mayoría de los miembros presentes). '
      + 'El quórum, el voto de calidad y los dos tercios para estatutos son comunes en estatutos, no obligatorios.',
    ruleSet: {
      version: 'v1.0',
      quorum: { type: 'percent', value: HALF, strict: true, atClose: 'none' },
      tie: 'casting',
      mode: 'nominal',
      majorities: {
        simple: majority('Mayoría de los presentes', 'present', HALF, true, true),
        estatutos: majority('Dos tercios de los presentes', 'present', TWO_THIRDS, false, true),
      },
      matters: [
        { id: 'acuerdo', label: 'Acuerdo de asamblea', majority: 'simple' },
        { id: 'estados', label: 'Aprobación de estados financieros', majority: 'simple' },
        { id: 'estatutos', label: 'Reforma de estatutos', majority: 'estatutos' },
        { id: 'consejo', label: 'Elección del consejo directivo', majority: 'simple', mode: 'secreta' },
      ],
    },
  },
];
